import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchCarePlanInstance,
  activeCarePlanDetailView,
  deleteCarePlan,
} from "../../../redux/actions/careplanActions";
import { usePublishWorkflow } from "../../../hooks/usePublishWorkflow";
// import { checkLockStatus } from "../../../services/dashboardServices";
import { clearCarePlan } from "../../../redux/slices/careplanSlice";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import UrgentCare from "./UrgentCare";
import Spacer from "../../common/Spacer";
import ItemThumbnail from "../../common/ItemThumbnail";
import FloatingMenu from "../../common/FloatingMenu";
import TextField from "../../common/TextField";
import { EditIcon, PublishIcon, DeleteIcon } from "../../common/Icons";
import EditCarePlan from "./EditCarePlan";
import { checkLockThen } from "../../../utils/checkLockUtils";

const ViewCarePlan = ({ carePlanId, onClose, version, published = false }) => {
  const {
    showValidationModal,
    isImportantInfoEmpty,
    showConfirm,
    showLockModal,
    lockedBy,
    setShowValidationModal,
    setShowLockModal,
    setLockedBy,
    handlePublish,
    confirmPublish,
    cancelPublish,
  } = usePublishWorkflow();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { sections, contacts, nhsNumber, important_info } = useSelector(
    (state) => state.careplan.carePlan
  );
  const userProfile = useSelector((state) => state.auth.userProfile);

  const [mode, setMode] = useState("view"); // "view" or "edit"
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [isLocking, setIsLocking] = useState(false);

  useEffect(() => {
    dispatch(clearCarePlan()); // Clear carePlan on Component Mount
    // Fetch the care plan instance using the provided carePlanId
    dispatch(fetchCarePlanInstance(carePlanId));
    if (published) dispatch(activeCarePlanDetailView(carePlanId));
  }, [carePlanId, published, dispatch]);

  // Actions for Floating Menu:
  const handleEdit = async () => {
    if (isLocking) return;
    setIsLocking(true);

    try {
      await checkLockThen(
        carePlanId,
        userProfile,
        dispatch,
        (lockedByUser) => {
          // If the care plan is locked by someone else, show the modal with their name
          setLockedBy(lockedByUser.name);
          setShowLockModal(true);
        },
        // onAlreadyLocked
        () => {
          // If the care plan is already locked by the current user, extend the lock
          setMode("edit");
        },
        // onProceed
        () => {
          // If the care plan is not locked, proceed to edit mode
          setMode("edit");
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      // ALWAYS reset isLocking, even if we “returned early” inside checkLockThen
      setIsLocking(false);
    }
  };

  const handleDelete = async () => {
    await checkLockThen(
      carePlanId,
      userProfile,
      dispatch,
      // onLocked
      (lockedBy) => {
        setLockedBy(lockedBy.name);
        setShowLockModal(true);
      },
      // onAlreadyLocked
      () => {
        setShowDeleteConfirm(true);
      },
      // onProceed
      () => {
        setShowDeleteConfirm(true);
      }
    );
  };

  const onPublishClick = () => handlePublish(carePlanId);

  const menuItems = [
    {
      label: "Edit",
      icon: <EditIcon color="#004F96" />,
      action: handleEdit,
    },
    {
      label: "Publish Plan",
      icon: <PublishIcon color="#004F96" />,
      action: onPublishClick,
    },
    {
      label: "Delete Plan",
      icon: <DeleteIcon color="#004F96" />,
      action: handleDelete,
    },
  ];

  if (!nhsNumber) {
    return <div>Loading...</div>; // You can replace this with a loader component
  }

  // const { sections = [], contacts = [], nhsNumber, important_info } = carePlan;
  if (mode === "edit") {
    return (
      <EditCarePlan
        carePlanId={carePlanId}
        nhsNumber={nhsNumber}
        onClose={() => setMode("view")}
        onSave={() => setMode("view")}
      />
    );
  }

  return (
    <FullScreenOverlay
      title={
        !published
          ? `Draft Care Plan: ${nhsNumber}`
          : `Digital Care Plan: ${nhsNumber}`
      }
      onClose={onClose}
      width="100%"
      height="100%"
      iconClose={true}
    >
      <FloatingMenu items={menuItems} position="bottom-center" />
      <FormSection>
        {sections
          .filter((section) => section.id !== 1 && section.items.length > 0)
          .map((section) => (
            <SectionContainer key={section.id}>
              <SectionTitleWrapper>
                <Rectangle color={section.color} />
                <h5 className="semi-bold neutral-900">{section.title}</h5>
              </SectionTitleWrapper>
              <Spacer height="16px" />

              {section.items.length > 0 ? (
                section.items
                  .filter((item) => item.answer && item.answer.trim() !== "") // Filter out items with empty answers
                  .map((item) => (
                    <SectionCard key={item.id}>
                      <ItemTitle>
                        <h6 className="semi-bold neutral-900">{item.title}</h6>
                      </ItemTitle>
                      <ItemCard>
                        <ItemThumbnail
                          src={item?.image_url}
                          alt={item?.image_alt_text || item?.title}
                          width={"150px"}
                          height={"150px"}
                          border={"1px solid #e9ecef"}
                        />

                        <ItemContent>
                          <p className="body-text-regular regular neutral-700">
                            {item.description}
                          </p>
                          <AnswerText>
                            {item.answer || "No answer provided"}
                          </AnswerText>
                        </ItemContent>
                      </ItemCard>
                    </SectionCard>
                  ))
              ) : (
                <p>No items in this section.</p>
              )}
              <HrLine />
            </SectionContainer>
          ))}

        {/* Urgent Care Section */}
        <SectionContainer>
          <SectionTitleWrapper>
            <Rectangle color="#EC2D30" />
            <h5 className="semi-bold neutral-900">
              Important Information if I need urgent care
            </h5>
          </SectionTitleWrapper>
          <Spacer height="16px" />

          <UrgentCare
            retrievedContacts={contacts}
            mode="view"
            initialImportantInfo={important_info}
          />
        </SectionContainer>
      </FormSection>

      {/* Show validation error if any */}
      {showValidationModal && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "Close",
              onClick: () => setShowValidationModal(false),
            },
          ]}
        >
          <h5 className="semi-bold">This care plan has empty items.</h5>
          {isImportantInfoEmpty ? (
            <p>
              Please fill "Important information if I need to go into hospital"
              to publish this care plan.
            </p>
          ) : (
            <p>Please complete to be able to publish this care plan</p>
          )}
        </FullScreenOverlay>
      )}

      {/* Confirmation modal for deleting the care plan */}
      {showDeleteConfirm && (
        <FullScreenOverlay
          width="480px"
          height="300px"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteConfirm(false);
                setDeleteText("");
              },
            },
            {
              label: "Yes, Delete",
              onClick: () => {
                if (deleteText === "Delete") {
                  // Use your draft care plan deletion logic here.
                  // You can pass type: published ? "active" : "draft"
                  dispatch(
                    deleteCarePlan({
                      carePlanId,
                      type: published ? "active" : "draft",
                    })
                  );
                  setShowDeleteConfirm(false);
                  setDeleteText("");
                }
              },
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm Care Plan Deletion</h5>
          <p>
            Are you sure you want to delete Care Plan {nhsNumber}? This action
            cannot be undone.
          </p>
          <p>
            Type <b>"Delete"</b> to confirm
          </p>
          <form onSubmit={(e) => e.preventDefault()}>
            <TextField
              placeholder=""
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value);
                e.target.setCustomValidity("");
              }}
              maxLength={10}
              required
              autoComplete="off"
            />
          </form>
        </FullScreenOverlay>
      )}

      {/* Confirmation modal for publishing the care plan */}
      {showConfirm && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => cancelPublish(),
            },
            {
              label: version > 1 ? "Yes, Replace" : "Yes, Publish",
              onClick: () =>
                confirmPublish({
                  carePlanId: carePlanId,
                  serviceUserName: "",
                  onSuccess: () =>
                    navigate("/admin_dashboard/active-care-plans"),
                }), // Handle delete confirmation
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Publish careplan?</h5>
          {version > 1 ? (
            <p>Are you sure you want to replace plan?</p>
          ) : (
            <p>Are you sure you want to make this draft care plan active?</p>
          )}
        </FullScreenOverlay>
      )}

      {
        // Show a modal if the care plan is locked
        showLockModal && (
          <FullScreenOverlay
            width="524px"
            height="225px"
            isSlimModal={true}
            isPopup={true}
            buttons={[
              {
                label: "OK",
                onClick: () => setShowLockModal(false),
              },
            ]}
          >
            <h5 className="semi-bold">Care Plan is locked</h5>
            <p>
              The care plan is currently being edited by <b>{lockedBy}</b>.
              Please try again later.
            </p>
          </FullScreenOverlay>
        )
      }
    </FullScreenOverlay>
  );
};

export default ViewCarePlan;

// Styled Components
const FormSection = styled.div`
  width: 60%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-100, #fff);
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const SectionCard = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-200, #f8f9fa);
`;

const SectionContainer = styled.div`
  margin-bottom: 32px;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const ItemTitle = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`;

const AnswerText = styled.div`
  display: flex;
  // flex: 1 0 0;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #ced4da);
  min-height: 96px;

  /* Dynamic height handling  *******NOT GROWING TO 150 NEED TO CHECK******* */
  // max-height: 150px; /* Set the maximum height for the description */
  overflow-y: auto; /* Add a scrollbar if the content exceeds the height */
  word-wrap: break-word; /* Ensure long words are wrapped */
  line-height: 1.2; /* Adjust line height to provide better readability */

  /* Allow the element to grow up to the max-height */
  height: auto; /* Let the height grow based on content */
  // min-height: 96px; /* Set an initial minimum height */

  /* Optional: Scrollbar styling for better visibility */
  // scrollbar-width: thin; /* Firefox */
  // scrollbar-color: #ced4da transparent; /* Firefox */

  // &::-webkit-scrollbar {
  //   width: 6px;
  // }

  // &::-webkit-scrollbar-thumb {
  //   background-color: #ced4da; /* Scrollbar color */
  //   border-radius: 10px;
  // }

  // &::-webkit-scrollbar-track {
  //   background: transparent; /* Scrollbar track color */
  // }
`;

const Rectangle = styled.div`
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 100px;
  background: ${({ color }) => color || "#3a70e2"};
  width: 4px;
  max-width: 4px;
  max-height: 32px;
`;

const HrLine = styled.div`
  height: 1px;
  background-image: repeating-linear-gradient(
    to right,
    #ccc,
    #ccc 10px,
    /* Dash length of 5px */ transparent 5px,
    transparent 25px /* Gap of 5px */
  );
`;
